import { queryOptions } from "@tanstack/react-query";

/**
 * Requête partagée de l'article prérendu d'un tirage (GET /_api/tirages/{date}/article).
 * Une seule définition (clé + fetch + retry) pour la page connectée, son
 * préchargement au niveau de la route et TiragePrerenderedArticle : tous
 * observent la même entrée de cache, donc une seule requête réseau.
 */
export interface TirageArticleData {
  html: string;
  /** Hash du mag-tirage.css servi, ajouté en ?v= sur le <link> (cache-busting). */
  cssVersion: string;
  /** Hash du km.css servi (mise en page 1.9.0 de l'article). */
  kmCssVersion: string;
}

export class TirageArticleError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "TirageArticleError";
  }
}

export const TIRAGE_ARTICLE_QUERY_KEY = "tirage-article";

export async function fetchTirageArticle(date: string): Promise<TirageArticleData> {
  const r = await fetch(`/_api/tirages/${date}/article`, {
    credentials: "include",
  });
  if (!r.ok) throw new TirageArticleError("article introuvable", r.status);
  const d = (await r.json()) as { html?: string; cssVersion?: string; kmCssVersion?: string };
  if (!d.html) throw new TirageArticleError("article vide", r.status);
  return { html: d.html, cssVersion: d.cssVersion ?? "dev", kmCssVersion: d.kmCssVersion ?? "dev" };
}

export const isValidTirageDate = (date: string | undefined): date is string =>
  !!date && /^\d{4}-\d{2}-\d{2}$/.test(date);

const PARIS_PARTS = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  hourCycle: "h23",
});

/** Date (YYYY-MM-DD) et heure (0-23) courantes à Paris, quel que soit le fuseau du navigateur. */
export function parisNow(now: Date = new Date()): { date: string; hour: number } {
  const parts: Record<string, string> = {};
  for (const p of PARIS_PARTS.formatToParts(now)) parts[p.type] = p.value;
  return { date: `${parts.year}-${parts.month}-${parts.day}`, hour: Number(parts.hour) };
}

/** Aujourd'hui à Paris, au format YYYY-MM-DD. */
export const parisToday = (now: Date = new Date()): string => parisNow(now).date;

/**
 * Tirage pas encore en base : date future, ou aujourd'hui avant 20h (le scrape
 * FDJ alimente la base à partir de 20:03). Date et heure sont celles de Paris,
 * pour que tous les utilisateurs (outre-mer, étranger) aient la même décision.
 * On affiche alors directement le teaser, sans requête vouée au 404.
 */
export function isTirageNotYetAvailable(date: string, now: Date = new Date()): boolean {
  if (!isValidTirageDate(date)) return false;
  const { date: today, hour } = parisNow(now);
  if (date > today) return true;
  return date === today && hour < 20;
}

export const tirageArticleQueryOptions = (date: string) =>
  queryOptions({
    queryKey: [TIRAGE_ARTICLE_QUERY_KEY, date],
    queryFn: () => fetchTirageArticle(date),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Un 404 (tirage absent) est définitif : pas de nouvelle tentative, le
    // teaser « tirage à venir » s'affiche tout de suite.
    retry: (failureCount, error) =>
      !(error instanceof TirageArticleError && error.status === 404) && failureCount < 2,
  });
